import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, MailCheck, MailWarning, LogOut } from "lucide-react";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

export function VerifyEmailGate() {
  const { user, logout } = useAuth();
  const [sending, setSending] = useState(false);
  const resend = async () => {
    setSending(true);
    try {
      await api.post("/auth/resend-verification");
      toast.success("Verification email sent - check your inbox.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send the email. Try again in a few minutes.");
    } finally {
      setSending(false);
    }
  };
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6">
      <div className="w-full max-w-md rounded-2xl border bg-card p-8 text-center shadow-sm" data-testid="verify-email-gate">
        <div className="flex justify-center"><Logo /></div>
        <MailCheck className="mx-auto mt-6 h-10 w-10 text-primary" />
        <h1 className="mt-4 text-xl font-bold text-foreground">Verify your email to continue</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          We sent a verification link to <b>{user?.email}</b>. Your investor portal unlocks once your email is confirmed.
        </p>
        <Button className="mt-6 w-full" onClick={resend} disabled={sending} data-testid="resend-verification-btn">
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Resend verification email"}
        </Button>
        <button
          className="mx-auto mt-4 inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground"
          onClick={logout}
          data-testid="verify-gate-logout"
        >
          <LogOut className="h-3.5 w-3.5" /> Sign out
        </button>
      </div>
    </div>
  );
}

export default function VerifyEmail() {
  const [state, setState] = useState("verifying");
  const [error, setError] = useState("");

  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get("token");
    if (!token) {
      setState("error");
      setError("This verification link is missing its token.");
      return;
    }
    api.post("/auth/verify-email", { token })
      .then(() => setState("success"))
      .catch((e) => {
        setState("error");
        setError(e?.response?.data?.detail || "This link is invalid or has expired.");
      });
  }, []);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6">
      <div className="w-full max-w-md rounded-2xl border bg-card p-8 text-center shadow-sm" data-testid="verify-email-page">
        <div className="flex justify-center"><Logo /></div>
        {state === "verifying" && <Loader2 className="mx-auto mt-8 h-8 w-8 animate-spin text-primary" data-testid="verify-loading" />}
        {state === "success" && (
          <>
            <MailCheck className="mx-auto mt-6 h-10 w-10 text-primary" />
            <h1 className="mt-4 text-xl font-bold text-foreground" data-testid="verify-success">Email verified</h1>
            <p className="mt-2 text-sm text-muted-foreground">Your investor portal is unlocked.</p>
            <Button className="mt-6 w-full" onClick={() => { window.location.href = "/portal"; }} data-testid="verify-continue-btn">
              Go to my portal
            </Button>
          </>
        )}
        {state === "error" && (
          <>
            <MailWarning className="mx-auto mt-6 h-10 w-10 text-destructive" />
            <h1 className="mt-4 text-xl font-bold text-foreground" data-testid="verify-error">Verification failed</h1>
            <p className="mt-2 text-sm text-muted-foreground">{error}</p>
            <Button variant="outline" className="mt-6 w-full" asChild>
              <Link to="/login">Go to sign in</Link>
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
